import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../../../api/apiClient';
import { usePreloaderStore } from '../../../../../stores';
import { useApiQuery } from '../../../../../api/hooks';
import { Endpoints, QueryKey } from '../../../../../views/democracy/constants';
import { extractErrorMessage } from '../../../../../views/democracy/utils';
import Modal from '../../controls/Modal';
import LabeledInput from '../../controls/LabeledInput';
import TeamsManagement from './settings/TeamsManagement';
import SystemSoundsSection from './settings/SystemSoundsSection';

const TABS = [
    { id: 'main', label: 'Основное' },
    { id: 'teams', label: 'Команды' },
    { id: 'sounds', label: 'Системные звуки' }
];

// Настройки игры (единая модалка, табы): Основное / Команды / Системные звуки.
export default function DemocracySettingsModal({ democracy, onChanged, onClose }) {
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const queryClient = useQueryClient();
    const [tab, setTab] = useState('main');
    const [title, setTitle] = useState(democracy.title || '');
    const [description, setDescription] = useState(democracy.description || '');

    const fullKey = QueryKey.democracyFull(democracy.id);
    const { data: full } = useApiQuery(fullKey, Endpoints.democracies.get(democracy.id), { refetchOnMount: 'always' });
    const invalidateFull = () => queryClient.invalidateQueries({ queryKey: fullKey });

    const handleSaveSettings = async () => {
        if (!title.trim()) {
            enqueueSnackbar('Название не может быть пустым', { variant: 'warning' });
            return;
        }
        show();
        try {
            await apiClient.put(Endpoints.democracies.update(democracy.id), {
                title: title.trim(),
                description: description.trim() || null,
                version: democracy.version
            });
            enqueueSnackbar('Сохранено', { variant: 'success' });
            if (onChanged) onChanged();
        } catch (error) {
            enqueueSnackbar(extractErrorMessage(error, 'Не удалось сохранить настройки'), { variant: 'error' });
        } finally {
            hide();
        }
    };

    const footer = <button type="button" className="btn btn-ghost" onClick={onClose}>Закрыть</button>;

    return (
        <Modal open kicker="Игра · настройки" title="Настройки игры" onClose={onClose} footer={footer}>
            <div className="tabs-view">
                {TABS.map(t => (
                    <button type="button" key={t.id} className={tab === t.id ? 'on' : undefined} onClick={() => setTab(t.id)}>
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === 'main' && (
                <>
                    <LabeledInput label="Название" value={title} onChange={(e) => setTitle(e.target.value)} />
                    <div>
                        <label className="ctl-label">Описание</label>
                        <textarea className="textarea" value={description} onChange={(e) => setDescription(e.target.value)}
                            placeholder="Короткое описание игры — увидят ведущие и игроки" />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button type="button" className="btn btn-primary" onClick={handleSaveSettings}>Сохранить настройки</button>
                    </div>
                </>
            )}

            {tab === 'teams' && <TeamsManagement democracy={democracy} />}
            {tab === 'sounds' && <SystemSoundsSection democracy={democracy} sounds={full?.systemSounds || []} onChanged={invalidateFull} />}
        </Modal>
    );
}

DemocracySettingsModal.propTypes = {
    democracy: PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string,
        description: PropTypes.string,
        version: PropTypes.number,
        status: PropTypes.string
    }).isRequired,
    onChanged: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};
